import { ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Typography } from "@mui/material";
import type { FC } from "react";
import { CONTROL_HEIGHT, FONT_SIZE, ICON_SIZE, ICON_TILE, INK, MOTION, NEUTRAL, SHADOW, SURFACE } from "../../../theme/tokens";
import type { CollapsibleSectionProps } from "./CollapsibleSection";
import { chevronSx } from "./collapsibleSectionSx";

type Props = Pick<CollapsibleSectionProps, "title" | "open" | "onToggle" | "children" | "icon" | "titleId" | "meta" | "dataTestId"> & { regionId: string };

/** `CollapsibleSection`'s `Card` variant: a raised card whose header carries a round brand icon, the title and trailing meta. */
export const CollapsibleCardSection: FC<Props> = ({ title, open, onToggle, children, icon, titleId, meta, regionId, dataTestId }) => (
	<Box sx={{ borderRadius: 1, backgroundColor: SURFACE, boxShadow: SHADOW.PANEL_SOFT, border: `1px solid ${NEUTRAL[150]}` }}>
		<Box
			component="button"
			type="button"
			onClick={onToggle}
			aria-expanded={open}
			aria-controls={regionId}
			data-testid={dataTestId}
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1.5,
				width: "100%",
				minHeight: CONTROL_HEIGHT.XL,
				px: 1.75,
				py: 1,
				border: "none",
				borderRadius: 1,
				background: "none",
				font: "inherit",
				textAlign: "start",
				cursor: "pointer",
				transition: `background-color ${MOTION.DURATION.COLOR_SHIFT}ms ${MOTION.EASE_OUT}`,
				"@media (hover: hover) and (pointer: fine)": { "&:hover": { backgroundColor: NEUTRAL[50] } },
				"&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
				"@media (prefers-reduced-motion: reduce)": { transition: "none" },
			}}
		>
			{icon !== undefined && (
				<Box
					component="span"
					aria-hidden
					sx={{
						width: ICON_TILE.XS.size,
						height: ICON_TILE.XS.size,
						borderRadius: "50%",
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						backgroundColor: "primary.main",
						color: INK.ON_DARK,
						"& > svg": { fontSize: ICON_SIZE.MICRO },
					}}
				>
					{icon}
				</Box>
			)}
			<Typography component="span" id={titleId} sx={{ flex: 1, minWidth: 0, fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: "primary.dark" }}>
				{title}
			</Typography>
			{meta !== undefined && (
				<Typography component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, whiteSpace: "nowrap" }}>
					{meta}
				</Typography>
			)}
			<ExpandMore aria-hidden sx={[chevronSx(open), { fontSize: ICON_SIZE.INLINE }]} />
		</Box>
		<Collapse in={open} id={regionId}>
			<Box sx={{ pb: 1 }}>{children}</Box>
		</Collapse>
	</Box>
);
